import { REEF_BOTTOM } from './data/reef/bottom';
import { HERBIVORES_KG_KM2 } from './data/reef/grazing';
import { held } from './held';
import { YEAR_DAYS } from './population';

/**
 * The herbivores' grazing (M167, the user's chain: "Fishing regulation → effective fishing mortality → survival and
 * population dynamics → herbivory → algal and coral responses"): the reef's bottom beside its coral, turf and
 * macroalgae, the space left the grazed epilithic algae and crustose coralline algae, as HIReefSim holds them for Maui
 * Nui (Weijerman et al. 2018, *Front. Mar. Sci.* 5: 425, CC BY 4.0, Supplementary Data Sheet 1, its equations and Table
 * S2), on CORSET's local model (Fung 2009; Melbourne-Thomas et al. 2011):
 *
 *   dT/dt = ζT (1 − θ) E − gT θ T − (coral growing and settling over turf) − γMT rM M T
 *   dM/dt = rM M E − gM θ M + γMC rM M C + γMT rM M T
 *   E = 1 − sediment − C − T − M
 *
 * with θ the grazing pressure, H / (iH + H), H the herbivorous fish's biomass and iH "A parameter that measures the
 * inaccessibility of algae (macroturf and macroalgae) to herbivorous fish grazing" (θ = θH + θU, θH = H / (iH + H),
 * Gurney et al. 2013, *PLoS ONE* 8: e80137, CC BY, Text S1, eqs. A3 to A5; HIReefSim has no urchins, "urchins ... were
 * not included", Weijerman et al. 2018, p. 3). The sediment is the surveys' and does not change.
 *
 * **Each stratum anchored at its survey** (`grazingAnchors`; the herbivores' year, `HERBIVORES_KG_KM2`, `npm run grazing`): NOAA's surveys' bottom, 2015 to 2024
 * (`REEF_BOTTOM`), is not ordered by the game's herbivores (Kahoʻolawe's 7.35 times Maui's biomass a km² of hard
 * bottom, its coralline algae no more than Maui's; MEASURED, the script), so one θ for all cannot hold it. Each stratum's
 * θ and its macroalgae's growth rM are those that hold its surveyed turf and macroalgae in balance beside its surveyed
 * coral, at its island's herbivores across the tables' year; the herbivores move it from there, its odds θ / (1 − θ) as
 * their biomass (H / iH, iH each stratum's own). What sets the strata apart beyond the herbivores (nutrients, waves,
 * the bottom's form) is in the anchors, not modeled (open). The iH they imply (`inaccessibilityKgKm2`), kg a km² of
 * hard bottom as the herbivores are, is 1.33e4 to 2.89e5 in ten strata, inside Table S2's 1e3 to 4e5 kg km⁻²;
 * Kahoʻolawe's middle and deep strata, light grazing at its island's high biomass, take 1.04e6 and 8.17e5, above it
 * (MEASURED, `tests/grazing.test.ts`; open). Table S2 does not say whether its km² counts sand: over each stratum's whole
 * floor, sand included, the two are 9.52e5 and 6.52e5, still above (the nenue among the herbivores since M178).
 */
export interface GrazingAnchor {
  /** The grazing pressure θ that holds the stratum's surveyed bottom in balance. */
  theta: number;
  /** Its macroalgae's growth over grazed EAC, rM, a year. */
  macroalgaeGrowthPerYear: number;
}

/**
 * The rates (Weijerman et al. 2018, Supplementary Data Sheet 1, Table S2, Maui Nui's column): each SET BY HAND at the
 * middle of its range but gM, at its floor (below), there being no measure of where in them Maui Nui's reefs lie. The
 * paper calibrated the others and drew gT and gM, with the fish's μM, μT, μE, iPH and gP, "from that range at random"
 * (Supplementary Text S2). The response to the herbivores barely depends on the two turf rates: Kahekili's coralline
 * algae and grazed EAC six years after its herbivores rose 2.39 times are 7.95% to 8.72% of the bottom across ζT 3 to 21
 * and gT 5 to 15, the four corners (8.57% at the middle, 8.59% since M176's coral by kind; MEASURED, the six years of `tests/grazing.test.ts`'s Kahekili
 * test, the anchors refit at each corner). At ζT 3 the lowest stratum's rM, 0.0075 a year, falls below Table S2's 0.01,
 * so the low end is the edge of what the ranges allow, not inside them.
 */
export const GRAZING = Object.freeze({
  /** ζT, "The rate at which grazed EAC grows into macroturf", 3 to 21 a year. */
  turfGrowthPerYear: 12,
  /** gT, "The maximum rate at which existing macroturf is grazed down", 5 to 15 a year. */
  turfGrazedPerYear: 10,
  /** gM over gT, 0.11 to 1: "11% of herbivorous fish eat macroalgae (CREP unpublished data)", the range's own floor. */
  macroalgaeGrazedShare: 0.11,
  /** αC, the coral's growth over turf relative to its growth over grazed EAC, 0 to 1. */
  coralOverTurf: 0.5,
  /** εC, the coral's recruitment onto turf relative to onto grazed EAC, 0.1 to 0.4. */
  recruitsOnTurf: 0.25,
  /** βM, the macroalgae's inhibition of the coral's growth, 1 − βM M, 0 to 0.3. */
  macroalgaeInhibition: 0.15,
  /** γMC and γMT, the macroalgae's growth over coral and over turf relative to over grazed EAC, 0 to 0.9 each. */
  macroalgaeOverCoral: 0.45,
  macroalgaeOverTurf: 0.45,
});

const PERCENT = 100;

/** A stratum's bottom as shares of it: sediment, coral, turf and macroalgae, the grazed EAC the rest. */
export interface Bottom {
  sediment: number;
  coral: number;
  turf: number;
  macroalgae: number;
}

export const grazedEac = (b: Bottom): number => Math.max(0, 1 - b.sediment - b.coral - b.turf - b.macroalgae);

/** A stratum's grazing pressure at its island's herbivores, its anchor's odds scaled by their biomass over the anchor's. */
export function grazingPressure(anchor: GrazingAnchor, herbivoreRatio: number): number {
  const odds = (anchor.theta / (1 - anchor.theta)) * herbivoreRatio;
  return odds / (1 + odds);
}

/**
 * What a stratum's bottom gives its coral beside its own space (`coral.ts`'s 1 − C/K, the amount): the kind of the
 * space, each against the stratum's surveyed kind, so the coral grows as it was fitted on the bottom the surveys found
 * (M167). Its colonies grow over grazed EAC and over turf at αC of it, held back by the macroalgae, 1 − βM M; its
 * recruits settle on grazed EAC and on turf at εC of it (Weijerman et al. 2018, Supplementary Data Sheet 1, its coral's
 * equation); and the macroalgae overgrow it, γMC rM M C, past what they did on the surveyed bottom (that already in
 * the coral's other losses).
 */
export interface CoralGround {
  growth: number;
  recruits: number;
  /** The macroalgae's overgrowth of the coral past the survey's, a year. */
  extraLossPerYear: number;
}

const surveyed = (i: number): Bottom => {
  const b = REEF_BOTTOM[i];
  if (!b) throw new Error(`no stratum ${i}`);
  return { sediment: b.sedimentPercent / PERCENT, coral: b.coralPercent / PERCENT, turf: b.turfPercent / PERCENT, macroalgae: b.macroalgaePercent / PERCENT };
};

/** The space's kind, a stratum's growth and recruits weights over its non-coral hard bottom. */
function kinds(b: Bottom): { growth: number; recruits: number } {
  const room = Math.max(1e-9, 1 - b.sediment - b.coral);
  const e = grazedEac(b) / room;
  const t = b.turf / room;
  return { growth: (1 - GRAZING.macroalgaeInhibition * b.macroalgae) * (e + GRAZING.coralOverTurf * t), recruits: e + GRAZING.recruitsOnTurf * t };
}

const SURVEYED_KINDS = REEF_BOTTOM.map((_, i) => kinds(surveyed(i)));

/** Each stratum's iH, kg a km² of hard bottom, its anchor's θ at its island's herbivores across the tables' year: H (1 − θ) / θ. */
export const inaccessibilityKgKm2 = (anchor: GrazingAnchor, island: number): number => ((HERBIVORES_KG_KM2[island] as number) * (1 - anchor.theta)) / anchor.theta;

/** The coral's rates a stratum's balance reads: r, ρ and K (`coral.ts`), a year and a share. */
export interface CoralGrowth {
  growthPerYear: number;
  recruitmentPerYear: number;
  ceiling: number;
}

/**
 * Each stratum's anchor (above): its θ and rM, solved so its surveyed turf and macroalgae do not change beside its
 * surveyed coral, that coral growing and settling over the turf as the game's coral does at that cover (its recruits
 * a year ρ C (1 − C/K), as Maui's when every island's cover is alike, `coral.ts`). From dM/dt = 0, rM = gM θ / (E +
 * γMC C + γMT T); with it dT/dt = 0 is linear in θ.
 */
export function grazingAnchors(coral: CoralGrowth): GrazingAnchor[] {
  const g = GRAZING;
  const gM = g.macroalgaeGrazedShare * g.turfGrazedPerYear;
  return REEF_BOTTOM.map((_, i) => {
    const b = surveyed(i);
    const e = grazedEac(b);
    const free = b.coral * (1 - b.coral / coral.ceiling);
    const overTurf = (w: number): number => (w * b.turf) / (e + w * b.turf);
    const coralOnTurf = coral.growthPerYear * free * overTurf(g.coralOverTurf) + coral.recruitmentPerYear * free * overTurf(g.recruitsOnTurf);
    const d = e + g.macroalgaeOverCoral * b.coral + g.macroalgaeOverTurf * b.turf;
    const theta = (g.turfGrowthPerYear * e - coralOnTurf) / (g.turfGrowthPerYear * e + g.turfGrazedPerYear * b.turf + (g.macroalgaeOverTurf * gM * b.macroalgae * b.turf) / d);
    return { theta, macroalgaeGrowthPerYear: (gM * theta) / d };
  });
}

/** A stratum's herbivores against its anchor's: its island's biomass a km² of hard bottom over the tables' year's (`HERBIVORES_KG_KM2`). */
export const herbivoreRatio = (island: number, kgKm2: number): number => kgKm2 / (HERBIVORES_KG_KM2[island] as number);

export function coralGround(i: number, b: Bottom, anchor: GrazingAnchor): CoralGround {
  const k = kinds(b);
  const s = SURVEYED_KINDS[i] as { growth: number; recruits: number };
  return {
    growth: k.growth / s.growth,
    recruits: k.recruits / s.recruits,
    extraLossPerYear: GRAZING.macroalgaeOverCoral * anchor.macroalgaeGrowthPerYear * (b.macroalgae - surveyed(i).macroalgae),
  };
}

/**
 * A stratum's turf and macroalgae as a world opens or a survey starts the coral (M167): its non-coral hard bottom
 * shared as the surveys found it, turf, macroalgae and grazed EAC, around the coral it has.
 */
export function bottomAround(i: number, coralPercent: number): { turfPercent: number; macroalgaePercent: number } {
  const b = REEF_BOTTOM[i];
  if (!b) throw new Error(`no stratum ${i}`);
  // Shared over the survey's whole non-coral hard bottom, its other points (invertebrates, soft coral) with the grazed
  // EAC as its anchor counts them (M167's reviewer: over turf, macroalgae and coralline algae alone, a stratum at its
  // survey's coral opened off its anchor).
  const surveyedRoom = PERCENT - b.sedimentPercent - b.coralPercent;
  const room = Math.max(0, PERCENT - b.sedimentPercent - coralPercent);
  return { turfPercent: held((room * b.turfPercent) / surveyedRoom), macroalgaePercent: held((room * b.macroalgaePercent) / surveyedRoom) };
}

/**
 * A day of a stratum's turf and macroalgae (the equations above), the coral's day given: its growth and its recruits,
 * shares of the bottom, and how much of each went over turf by its weights (CORSET's split of the coral's growth over
 * grazed EAC and over turf). The coral's deaths leave grazed EAC, the rest.
 */
export function nextBottom(b: Bottom, anchor: GrazingAnchor, theta: number, coralGrowth: number, coralRecruits: number): { turf: number; macroalgae: number } {
  const g = GRAZING;
  const e = grazedEac(b);
  const rM = anchor.macroalgaeGrowthPerYear;
  const overTurf = (w: number): number => (e + w * b.turf > 0 ? (w * b.turf) / (e + w * b.turf) : 0);
  const coralOnTurf = coralGrowth * overTurf(g.coralOverTurf) + coralRecruits * overTurf(g.recruitsOnTurf);
  const dT = (g.turfGrowthPerYear * (1 - theta) * e - g.turfGrazedPerYear * theta * b.turf - g.macroalgaeOverTurf * rM * b.macroalgae * b.turf) / YEAR_DAYS - coralOnTurf;
  const dM = (rM * b.macroalgae * e - g.macroalgaeGrazedShare * g.turfGrazedPerYear * theta * b.macroalgae + g.macroalgaeOverCoral * rM * b.macroalgae * b.coral + g.macroalgaeOverTurf * rM * b.macroalgae * b.turf) / YEAR_DAYS;
  const turf = Math.max(0, b.turf + dT);
  const macroalgae = Math.max(0, b.macroalgae + dM);
  // Nothing grows past the stratum's hard bottom: the turf gives way where the day's growth would.
  const over = b.sediment + b.coral + turf + macroalgae - 1;
  return { turf: over > 0 ? Math.max(0, turf - over) : turf, macroalgae };
}
